import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Youtube, Camera } from 'lucide-react';
import { API, BACKEND_URL } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import ShareButtons from '../components/ShareButtons';

const MediaPage = () => {
  const [albums, setAlbums] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [activeTab, setActiveTab] = useState('photos');
  const [mediaSeason, setMediaSeason] = useState(2);
  const { t, language } = useLanguage();

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setSelectedPhoto(null); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  useEffect(() => {
    axios.get(`${API}/gallery?season=${mediaSeason}`).then(res => setAlbums(res.data)).catch(console.error);
    axios.get(`${API}/videos?season=${mediaSeason}`).then(res => setVideos(res.data)).catch(console.error);
  }, [mediaSeason]);

  const getPhotoSrc = (photo) => {
    if (photo.is_upload) return `${BACKEND_URL}${photo.url}`;
    return photo.url;
  };

  const getYoutubeEmbedId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="media-page">
      <div className="text-center mb-8">
        <h1 className="section-title mb-4">Media</h1>
      </div>

      {/* Season Tabs */}
      <div className="flex justify-center gap-3 mb-6" data-testid="media-season-tabs">
        <button onClick={() => setMediaSeason(1)} className={`px-5 py-2.5 rounded-lg font-semibold transition ${mediaSeason === 1 ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="media-season1-tab">
          {language === 'gr' ? 'Σεζόν 1' : 'Season 1'}
        </button>
        <button onClick={() => setMediaSeason(2)} className={`px-5 py-2.5 rounded-lg font-semibold transition ${mediaSeason === 2 ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="media-season2-tab">
          {language === 'gr' ? 'Σεζόν 2' : 'Season 2'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex justify-center gap-4 mb-10">
        <button 
          onClick={() => setActiveTab('photos')}
          className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition ${activeTab === 'photos' ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="media-tab-photos"
        >
          <Camera size={18} /> {t('photos')}
        </button>
        <button 
          onClick={() => setActiveTab('videos')}
          className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition ${activeTab === 'videos' ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="media-tab-videos"
        >
          <Youtube size={18} /> {t('videos')}
        </button>
      </div>

      {/* Photos Tab */}
      {activeTab === 'photos' && (
        <div>
          {albums.length === 0 ? (
            <p className="text-slate-500 text-center py-12">{t('noPhotos')}</p>
          ) : (
            albums.map(album => (
              <div key={album.id} className="mb-12" data-testid={`gallery-album-${album.id}`}>
                <h2 className="text-xl font-display font-bold text-[#1E3A5F] mb-2 flex items-center">
                  <Camera className="mr-2" size={24} />
                  {album.title} - {t('week')} {album.week}
                </h2>
                {album.description && <p className="text-slate-500 text-sm mb-4">{album.description}</p>}
                {album.photos.length === 0 ? (
                  <p className="text-slate-400 text-sm">{t('noPhotos')}</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {album.photos.map(photo => (
                      <div key={photo.id} className="relative group cursor-pointer rounded-lg overflow-hidden aspect-square" onClick={() => setSelectedPhoto(photo)}>
                        <img 
                          src={getPhotoSrc(photo)} 
                          alt={photo.caption || ''} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {photo.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                            <p className="text-white text-sm">{photo.caption}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Videos Tab */}
      {activeTab === 'videos' && (
        <div>
          {videos.length === 0 ? (
            <p className="text-slate-500 text-center py-12">{t('noVideos')}</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {videos.map(video => {
                const embedId = getYoutubeEmbedId(video.youtube_url);
                return (
                  <div key={video.id} className="card overflow-hidden" data-testid={`video-card-${video.id}`}>
                    {embedId ? (
                      <div className="aspect-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${embedId}`}
                          title={video.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="block aspect-video bg-slate-100 flex items-center justify-center">
                        <Youtube size={48} className="text-red-500" />
                      </a>
                    )}
                    <div className="p-4">
                      <h3 className="font-display font-bold text-lg text-[#1E3A5F] mb-1">{video.title}</h3>
                      {video.description && <p className="text-sm text-slate-600">{video.description}</p>}
                      <p className="text-xs text-slate-400 mt-2">{new Date(video.created_at).toLocaleDateString('el-GR')}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <ShareButtons title={language === 'gr' ? 'Media WBLA' : 'WBLA Media'} />
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPhoto(null)} data-testid="gallery-lightbox">
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setSelectedPhoto(null)}><X size={32} /></button>
          <img 
            src={getPhotoSrc(selectedPhoto)} 
            alt={selectedPhoto.caption || ''} 
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          {selectedPhoto.caption && (
            <p className="absolute bottom-8 text-white text-center text-lg">{selectedPhoto.caption}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaPage;
